import {
  queryOptions,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { LoaderCircle } from 'lucide-react';

import { UpdateNoteForm } from '@components/Notes/UpdateForm';
import { PageTitle } from '@components/PageTitle';
import { queryKeys } from '@constants/queryKeys.constant';
import { EStaleTime } from '@enums/staleTime.enum';
import { updateServiceRequestCounts } from '@helpers/queryCache/updateTabCounts.helper';
import { noteService } from '@services/notes/service';

function noteQueryOptions(id: string) {
  return queryOptions({
    queryFn: () => noteService.getById(id),
    queryKey: queryKeys.notes.detail(id),
    staleTime: EStaleTime.OneHour,
  });
}

export const Route = createFileRoute(
  '/_layout/(private)/service-requests/$id/notes/$noteId/update',
)({
  component: ServiceRequestNoteUpdate,
  loader: ({ context, params }) => {
    const options = noteQueryOptions(params.noteId);
    return context.queryClient.ensureQueryData({
      ...options,
      revalidateIfStale: true,
    });
  },
  pendingComponent: () => (
    <div className="flex min-h-dvh w-full items-center justify-center">
      <LoaderCircle className="text-primary animate-spin" size={80} />
    </div>
  ),
  pendingMinMs: 600,
  pendingMs: 300,
});

function ServiceRequestNoteUpdate() {
  const { id: serviceRequestId, noteId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data } = useSuspenseQuery(noteQueryOptions(noteId));

  return (
    <>
      <PageTitle
        hasBackButton
        params={{ id: serviceRequestId }}
        title="Update note"
        to="/service-requests/$id"
      />
      <UpdateNoteForm
        note={data.note}
        onCancel={() =>
          navigate({
            params: { id: serviceRequestId },
            to: '/service-requests/$id',
          })
        }
        onDelete={() =>
          updateServiceRequestCounts({
            delta: -1,
            field: 'notes',
            queryClient,
            serviceRequestId,
          })
        }
        onSuccess={() =>
          navigate({
            params: { id: serviceRequestId },
            replace: true,
            to: '/service-requests/$id',
          })
        }
        queryKeyToInvalidate={queryKeys.serviceRequests.notes(serviceRequestId)}
      />
    </>
  );
}
